import { useEffect, createElement, useState, KeyboardEventHandler, useRef, Fragment } from 'react';
import { Scrollable } from '../Scrollable';
import { Icon } from '../Icon';
import { MultiBreadcrumbDesign, SingleBreadcrumbDesign } from './BreadcrumbItem';
import { useDesignTokens, renderInlineCssVars } from '../../theme';
import { useRefArray } from '../../hooks/useRefArray';
import { DisclosureNextBreadcrumb } from '@sas/react-svg';
import bundle from './NovaComponents-HorizontalBreadcrumb-gui-icu.properties';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useIsRTL } from '../../locale';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { HorizontalBreadcrumbProps } from '.';
import { postfixTestId } from '../../util/testId';
import cx from 'classnames';

import cn from './HorizontalBreadcrumb.css.json';
import './HorizontalBreadcrumb.css';

const useTextLocalizer = createUseTextLocalizer(bundle);
const useAriaTextId = createUseHtmlId('AriaText');

export const HorizontalBreadcrumb = (props: HorizontalBreadcrumbProps) => {
  const localize = useTextLocalizer();
  const listAriaTextId = useAriaTextId();
  const lastValueIndex = props.values ? props.values.length - 1 : -1;

  const refIsMultiItemOpen = useRef(false);

  const [focusedIndex, setFocusedIndex] = useState(lastValueIndex);
  const [horizontalScrollIndex, setHorizontalScrollIndex] = useState(lastValueIndex);
  const [listMaxWidth, setListMaxWidth] = useState<number>(400);

  const [setScrollableContentRef, getScrollableContent] = useResizeObserverRef<HTMLDivElement>(
    () => {
      const scrollableContent = getScrollableContent();
      if (!scrollableContent) return;
      setListMaxWidth(scrollableContent.clientWidth);
    }
  );
  useEffect(() => {
    // If number of values changes, scroll to the last item in the list
    setHorizontalScrollIndex(lastValueIndex);
    setFocusedIndex(lastValueIndex);
  }, [lastValueIndex]);

  const dt = useDesignTokens();
  let itemIconTokens = dt.enableNovaTokens ? dt.c.colorTextLabel : dt.stateStandard.normal.fg;

  if (dt.themeVariant === 'light' && props.variant === 'primary-light') {
    itemIconTokens = dt.c.colorTextInverse;
  }
  const spacingTokens = renderInlineCssVars(
    {
      'squish-xs': dt.spacing.insetSquish.xs.padding,
      'squish-s': dt.spacing.insetSquish.s.padding,
      'anim-dur-enter': dt.animation.duration.enter.s,
      'anim-tf-enter': dt.animation.timingFunction.enter.m,
      'mg-xs': dt.spacing.core.xs,
      'max-width': `${Math.max(lastValueIndex === 0 ? listMaxWidth : listMaxWidth - 64, 400)}px`,
    },
    'breadcrumb'
  );
  const isRTL = useIsRTL();

  const onKeyDown: KeyboardEventHandler = (event) => {
    if (refIsMultiItemOpen.current) {
      return;
    }

    switch (event.key) {
      case isRTL ? 'ArrowRight' : 'ArrowLeft': {
        const nextFocusedIndex = Math.max(focusedIndex - 1, 0);
        setFocusedIndex(nextFocusedIndex);
        break;
      }
      case isRTL ? 'ArrowLeft' : 'ArrowRight': {
        const nextFocusedIndex = Math.min(focusedIndex + 1, lastValueIndex);
        setFocusedIndex(nextFocusedIndex);
        break;
      }
      case 'PageUp': {
        const nextFocusedIndex = Math.max(
          getNextPageFocusedItemIndex(refContent.current, breadcrumbRefs, 'back') ?? focusedIndex,
          0
        );
        setFocusedIndex(nextFocusedIndex);
        break;
      }
      case 'PageDown': {
        const nextFocusedIndex = Math.min(
          getNextPageFocusedItemIndex(refContent.current, breadcrumbRefs, 'next') ?? focusedIndex,
          lastValueIndex
        );
        setFocusedIndex(nextFocusedIndex);
        break;
      }
      case 'Home':
        setFocusedIndex(0);
        break;
      case 'End':
        setFocusedIndex(lastValueIndex);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const refContainer = useRef<HTMLDivElement | null>(null);
  const refContent = useRef<HTMLElement>(null);

  const setContainerRef = (el: HTMLDivElement | null) => {
    refContainer.current = el;
    setScrollableContentRef(el);
  };

  const { refs: breadcrumbRefs, createSetRef: createBreadcrumbRef } = useRefArray<
    HTMLDivElement | HTMLAnchorElement
  >();

  useEffect(() => {
    if (refContainer.current?.contains(document.activeElement)) {
      breadcrumbRefs[focusedIndex]?.focus();
    }
  }, [breadcrumbRefs, focusedIndex]);

  return (
    <Fragment>
      <Scrollable
        ref={setContainerRef}
        contentRef={refContent}
        currentIndex={horizontalScrollIndex}
        setCurrentIndex={setHorizontalScrollIndex}
        separator={
          <Icon
            className={cx(cn.itemIcon, cn.opening)}
            tokens={() => ({
              fg: itemIconTokens,
            })}
            icon={DisclosureNextBreadcrumb}
          />
        }
        contentClassName={cn.list}
        contentProps={{ onKeyDown }}
        style={{ ...spacingTokens, ...props.style }}
        selectedIndex={props.onPress ? horizontalScrollIndex : undefined}
        onFocus={(event) => {
          if (
            event.currentTarget.contains(event.target) &&
            event.relatedTarget !== null && // the relatedTarget is null when the element's tabindex is undefined or -1 in Safari browser
            !event.currentTarget.contains(event.relatedTarget as Node)
          ) {
            setHorizontalScrollIndex(focusedIndex);
          }
        }}
        variant={props.variant === 'primary-light' ? 'primary-light' : 'standard'}
        contentAriaDescribedBy={listAriaTextId}
        allowOrderedList
        testId={postfixTestId(props.testId, 'scrollable')}
      >
        {props.values?.map((value, i) => {
          const focused = i === focusedIndex;
          const breadcrumbRef = createBreadcrumbRef(i);
          return value.isSingle ? (
            <SingleBreadcrumbDesign
              ref={breadcrumbRef}
              key={i}
              index={i}
              animated={i > 0}
              focused={focused}
              isCurrent={i === lastValueIndex}
              value={value.values[value.selectedIndex]}
              onPress={(index) => {
                setFocusedIndex(index);
                props.onPress?.(index);
              }}
              variant={props.variant}
              testId={postfixTestId(props.testId, `breadcrumbItem-${i}`)}
              onFocus={() => {
                setHorizontalScrollIndex(i);
              }}
              tokens={props.tokens}
            />
          ) : (
            <MultiBreadcrumbDesign
              ref={breadcrumbRef}
              key={i}
              index={i}
              isCurrent={i === lastValueIndex}
              focused={focused}
              values={value.values}
              onOpen={(index) => {
                refIsMultiItemOpen.current = true;
                setFocusedIndex(index);
                props.onOpen?.(index);
              }}
              onClose={(index) => {
                refIsMultiItemOpen.current = false;
                setFocusedIndex(index);
              }}
              onPress={(index) => {
                setFocusedIndex(index);
                props.onPress?.(index);
              }}
              onPressItem={props.onPressItem}
              selectedIndex={value.selectedIndex}
              onFocus={() => {
                setHorizontalScrollIndex(i);
              }}
              variant={props.variant}
              testId={postfixTestId(props.testId, `breadcrumbItem-${i}`)}
              tokens={props.tokens}
            />
          );
        })}
      </Scrollable>
      <span id={listAriaTextId} hidden>
        {localize('list.aria')}
      </span>
    </Fragment>
  );
};

// First, create a map in which full visible items are marked as true, otherwise as false.
// From this map can get the first/last item (base on the operation is next or back) in next page easily.
// Then, get the last/first item in next page by comparing the possible items' total wdith to container's width
function getNextPageFocusedItemIndex(
  container: HTMLElement | null,
  items: ReadonlyArray<HTMLElement | null>,
  direction: 'back' | 'next'
): number | null {
  if (!container) return null;

  const containerRect = container.getBoundingClientRect();
  const itemVisibilityMap = items.map((item) => {
    if (!item) return false;
    const itemRect = item.getBoundingClientRect();
    return (
      itemRect.left >= containerRect.left &&
      itemRect.left + itemRect.width <= containerRect.left + containerRect.width
    );
  });

  const startIndex =
    direction === 'back'
      ? itemVisibilityMap.indexOf(true) - 1
      : itemVisibilityMap.lastIndexOf(true) + 1;
  const endIndex = direction === 'back' ? 0 : items.length - 1;
  const step = direction === 'back' ? -1 : 1;

  let itemsWidth = 0;
  let focusIndex = endIndex;
  for (let i = startIndex; i !== endIndex; i += step) {
    itemsWidth += items[i]?.getBoundingClientRect().width ?? 0;
    if (itemsWidth > containerRect.width) {
      focusIndex = i - step;
      break;
    }
    itemsWidth += 14; // plus separator width which is a fixed value
  }

  return focusIndex;
}
